import {
  difference,
  extend,
  omitBy,
  each,
  pickBy,
  without,
  map,
  union,
  isEmpty,
  find,
  mapValues,
  uniq,
  some,
  omit,
  isEqual,
  pull,
  flow,
  keys,
} from 'lodash';
import { curriedPick, curriedOmitBy, curriedReject, compose } from '@adjust/dashboard-core/lib/fp';

/**
   * @ngdoc service
   * @name adjust.common.service:Filter
   *
   * @description
   * Service used with filter directive within the statistics pages
   *
   */
function Filter(
  platforms,
  regions,
  countries,
  filterConstants,
  Grouping,
  $location,
  TimeSpan,
  Moment,
  $timeout,
  CustomPeriod,
  App
) {
  'ngInject';
  /**
   * @ngdoc property
   * @name _allowedParams
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * List of parameters allowed to change
   *
   * @type {Array}
   * @private
   */
  var _allowedParams = ['os_names', 'platforms', 'regions', 'countries', 'sandbox', 'attribution_type', 'view', 'base', 'kpi', 'gkpi', 'grouping', 'scale', 'columns', 'pinned', 'plotted', 'conversion_type', 'timezone_id', 'granularity', 'attribution_source', 'secret_ids', 'partner_ids'];
  var _allowedGParams = ['gkpi', 'scale', 'granularity'];
  var _outerParams = ['plotted', 'pinned', 'gkpi', 'scale', 'view', 'granularity', 'columns'];
  var _excludableParams = ['countries', 'regions'];
  var _excludedParams = [];

  /**
   * @ngdoc property
   * @name _alwaysOnUrl
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * The params that always have to be on the url
   *
   * @type {Array}
   * @private
   */
  var _alwaysOnUrl = ['timezone_id'];

  /**
   * @ngdoc property
   * @name _params
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * Main parameters object
   *
   * @type {Object}
   * @private
   */
  var _params = {};

  /**
   * @ngdoc property
   * @name _screen
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * Current screen where filter directive has been loaded
   *
   * @type {string}
   * @private
   */
  var _screen;

  /**
   * @ngdoc property
   * @name _type
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * Type of the statistics, can be overview or cohorts
   *
   * @type {string}
   * @private
   */
  var _type;

  /**
   * @ngdoc property
   * @name _previousScreen
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * Previous screen where filter directive has been loaded
   *
   * @type {string}
   * @private
   */
  var _previousScreen;

  /**
   * @ngdoc property
   * @name _offPlatform
   * @propertyOf adjust.common.service:Filter
   *
   * @description
   * Off platform name
   *
   * @type {string}
   * @private
   */
  var _offPlatform = 'off-platform';
  var _otherOs = 'other';

  var _arrays = ['os_names', 'platforms', 'regions', 'countries', 'columns', 'pinned', 'plotted', 'partner_ids'];
  var _booleans = ['sandbox', 'rejected'];
  var _numbers = ['timezone_id'];

  return {
    init: init,
    getParams: getParams,
    getParam: getParam,
    getBase: getBase,
    preserve: preserve,
    preserveParam: preserveParam,
    deleteParam: deleteParam,
    getGParams: getGParams,
    setGParams: setGParams,
    prepareOsNames: prepareOsNames,
    getRegions: getRegions,
    getCountries: getCountries,
    getPeriod: getPeriod,
    getBases: getBases,
    getAttributionSource: getAttributionSource,
    getConversionType: getConversionType,
    getImpressionType: getImpressionType,
    getGroupings: getGroupings,
    encodeParams: encodeParams,
    adjustParams: adjustParams,
    resetParams: resetParams,
    isByDefault: isByDefault,
    getDefaultSort: getDefaultSort,
    addPin: addPin,
    removePin: removePin,
    addPlot: addPlot,
    removePlot: removePlot,
    getOuterParams: getOuterParams,
    resetGrouping: resetGrouping,
    getGrouping: getGrouping,
    currentMomentTz: currentMomentTz,
    addExcludableParam: addExcludableParam,
    removeExcludableParam: removeExcludableParam,
    isParamExcluded: isParamExcluded,
    clearEmptyExcludableParams: clearEmptyExcludableParams,
    getPlatforms: getPlatforms,
    _getSelectedOsNames: _getSelectedOsNames,
    _getPlatform: _getPlatform
  };

  /**
   * @ngdoc method
   * @name _defaults
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Return promise with the filter defaults
   *
   * @private
   * @returns promise with the filter defaults
   */
  function _defaults() {

    return Moment.defaultTimezone()
      .then(function(timezone) {

        return {
          graph: {
            // gkpi: 'installs', // Default value of the gkpi depends on the screen
            scale: 'sqrt',
            // granularity: // Default value will be set after TimeSpan initialization
          },
          overview: {
            os_names: [],
            platforms: [],
            countries: [],
            partner_ids: [],
            sandbox: false,
            attribution_type: 'click',
            columns: [],
            pinned: [],
            plotted: [],
            grouping: 'trackers',
            timezone_id: timezone.id,
            conversion_type: 'all',
            attribution_source: 'dynamic'
          },
          cohorts: {
            os_names: [],
            platforms: [],
            countries: [],
            partner_ids: [],
            sandbox: false,
            base: 'all',
            attribution_type: 'click',
            conversion_type: 'all',
            columns: [],
            pinned: [],
            plotted: [],
            grouping: 'trackers',
            timezone_id: timezone.id,
            attribution_source: 'dynamic'
          },
          secrets: {
            countries: [],
            sandbox: false,
            plotted: [],
            grouping: 'secret_ids',
            timezone_id: timezone.id
          }
        };

      });

  }

  /**
   * @ngdoc method
   * @name _prepareParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Prepare default parameters and extend them with query ones if exist
   *
   * @private
   */
  function _prepareParams() {

    return _defaults()
      .then(function(defaults) {

        var queryParams = _decodeParams();
        var opposite = _type === 'cohorts' ? 'overview' : 'cohorts';
        var diff = difference(Object.keys(defaults[opposite]), Object.keys(defaults[_type]));

        _params = angular.copy(defaults[_type]);
        extend(_params, defaults.graph);

        extend(_params, omitBy(queryParams, function(value, key) {
          return diff.includes(key);
        }));

        if (!_params.gkpi || _isScreenChanged()) {
          defaults.graph.gkpi = getDefaultSort();
          _params.gkpi = defaults.graph.gkpi;
        }

        each(_alwaysOnUrl, function(key) {
          preserveParam(key, _params[key]);
        });

      });

  }

  /**
   * @ngdoc method
   * @name _isScreenChanged
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Checks if screen is changed
   *
   * @returns {Boolean}
   * @private
   */
  function _isScreenChanged() {

    return _previousScreen && _screen !== _previousScreen;

  }

  /**
   * @ngdoc method
   * @name init
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Set current screen for the filters and initiate parameters preparation
   *
   * @param {string} screen current screen where filters are loaded
   */
  function init(screen) {

    _previousScreen = _screen;
    _screen = screen;
    _type = [
      'cohorts',
      'overview',
      'secrets'
    ].includes(screen) ? screen : 'overview';

    return _prepareParams();

  }

  /**
   * @ngdoc method
   * @name getParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get the copy of the current parameters and prepare  additional one for cohorts
   *
   * @returns {Object} copy of current parameters
   */
  function getParams() {

    return angular.copy(_params);

  }

  function getParam(key) {

    return angular.copy(_params[key]);

  }

  function getBase() {

    if (_params['base'] === 'all') {
      return 'all';
    }

    return (_params['base'] && 'events') || 'all';

  }

  /**
   * @ngdoc method
   * @name preserve
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Preserve parameters
   *
   * @param {Object} params params to be preserved
   */
  function preserve(params) {

    _params = angular.copy(params);

    // XXX: please don't set params by using `$location.search(params)` here, too many side effects due to the `$location.search` watch
    // HACK: $timeout is used because it appears that replace() replaces the entire route record when in current digest cycle
    $timeout(function() {
      each(encodeParams(), function(value, key) {
        $location.search(key, value).replace();
      });
    });

  }

  /**
   * @ngdoc method
   * @name preserveParam
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Preserve particular parameter
   *
   * @param {string} key to be set
   * @param {*} value value to be set
   * @returns the newly set value
   */
  function preserveParam(key, value) {

    _params[key] = value;

    // HACK: $timeout is used because it appears that replace() replaces the entire route record when in current digest cycle
    $timeout(function() {
      $location.search(key, encodeParam(value, key)).replace();
    });

    return value;

  }

  /**
   * @ngdoc method
   * @name deleteParam
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Delete a param from route and api call
   *
   * @param {string} key to be deleted
   */
  function deleteParam(key) {

    delete _params[key];

    // HACK: $timeout is used because it appears that replace() replaces the entire route record when in current digest cycle
    $timeout(function() {
      $location.search(key, null).replace();
    });

  }

  /**
   * @ngdoc method
   * @name getGParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get current filters for graph
   *
   * @returns {Object} graph parameters
   */
  function getGParams() {

    return pickBy(_params, function(value, key) {
      return _allowedGParams.includes(key);
    });

  }

  /**
   * @ngdoc method
   * @name setGParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Set parameters for graph filtering
   *
   * @param {Object} params parameters to set
   */
  function setGParams(params) {

    // XXX: please don't set params by using `$location.search(params)` here, too many side effects due to the `$location.search` watch
    // HACK: $timeout is used because it appears that replace() replaces the entire route record when in current digest cycle
    $timeout(function() {
      each(params, function(value, key) {
        _params[key] = value;
        $location.search(key, value).replace();
      });
    });

  }

  /**
   * @ngdoc method
   * @name resetParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Reset parameters to default
   *
   * @returns {Promise} after the reset finished
   */
  function resetParams() {

    var params = _decodeParams();
    var outer = without(_outerParams, 'columns');

    _excludedParams = [];

    // XXX: please don't set params by using `$location.search(params)` here, too many side effects due to the `$location.search` watch
    // HACK: $timeout is used because it appears that replace() replaces the entire route record when in current digest cycle
    return $timeout(function() {
      each(params, function(value, key) {
        if (!outer.includes(key)) {
          $location.search(key, null).replace();
        }
      });
    }).then(_prepareParams);

  }

  /**
   * Get the list of os names to filter by
   *
   * @param {Array} osNames
   * @param {Array=[]} selectedOsNames
   * @returns {String} list of os names to query by
   * @private
   */
  function _getSelectedOsNames(osNames, selectedOsNames = []) {
    const otherPlatformsList = without(platforms, ...osNames);

    return map(selectedOsNames, osName =>
      osName === _otherOs ? otherPlatformsList : osName).join(',');
  }

  function _getPlatform(selectedPlatform = []) {
    const { default_attribution_platform } = App.getCurrentApp();
    const offPlatformName = {
      web: 'mobile_app',
      mobile_app: 'web'
    }[default_attribution_platform];

    return map(selectedPlatform, platform =>
      platform === _offPlatform ? offPlatformName : platform).join(',');
  }

  /**
   * Get the list of os names to filter by
   *
   * {Array} list of available platforms
   */
  function prepareOsNames(appPlatforms) {

    if (!appPlatforms || appPlatforms.includes(_otherOs)) {
      return;
    }

    appPlatforms.push(_otherOs);

  }

  /**
   * @param {Number} accountId
   * @returns {Array}
   */
  function getRegions(accountId) {

    // TODO: Remove fixed condition for Viber
    if (accountId === 6677) {
      return regions.VIBER;
    }

    return regions.ALL;

  }

  /**
   * @ngdoc method
   * @name getCountries
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get the list of available countries
   *
   * @returns {Array} list of available countries
   */
  function getCountries() {

    return angular.copy(countries);

  }

  /**
   * @ngdoc method
   * @name getBases
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get list of available bases which consists of event types and two custom kpis (all and paying users)
   *
   * @param {Array} eventTypes event types array to be merged with base
   * @returns {Array} list of available bases
   */
  function getBases(eventTypes) {

    return union(filterConstants.kpiBasis, eventTypes);

  }

  /**
   * Get the currently selected period
   *
   * @returns {String}
   */
  function getPeriod() {
    const { period } = CustomPeriod.getParams();
    return period;
  }

  /**
   * @ngdoc method

  /**
   * @ngdoc method
   * @name getAttributionSource
   * @methodOf adjust.statistics.service:Filter
   *
   * @description
   * Get list of available attribution source for all statistics
   *
   * @returns {Array} list of available attribution sources
   */
  function getAttributionSource() {

    return angular.copy(filterConstants.attributionSource);

  }

  /**
   * @ngdoc method
   * @name getConversionType
   * @methodOf adjust.statistics.service:Filter
   *
   * @description
   * Get list of available conversion types
   *
   * @returns {Array} list of conversion type options
   */
  function getConversionType() {

    return angular.copy(filterConstants.conversionType);

  }

  /**
   * @ngdoc method
   * @name getImpressionType
   * @methodOf adjust.statistics.service:Filter
   *
   * @description
   * Get list of available impression types
   *
   * @returns {Array} list of impression type options
   */
  function getImpressionType() {

    return angular.copy(filterConstants.impressionType);

  }

  /**
   * @ngdoc method
   * @name getGroupings
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get list of available groupings for the type of statistics
   *
   * @params {string} view table or graph
   *
   * @returns {Array} list of available groupings
   */
  function getGroupings({ view, hasWebAttributionPackage = false} = {}) {
    const groupings = Grouping.get(_type);

    return compose(
      curriedReject({hide: view}),
      curriedReject(grouping => grouping.value === 'platforms' && !hasWebAttributionPackage)
    )(groupings);
  }

  function getPlatforms() {
    const { default_attribution_platform } = App.getCurrentApp();

    const options = {
      mobileApp: {
        value: 'mobile_app',
        label: gettext('label.mobile_app'),
      },
      web: {
        value: 'web',
        label: gettext('label.web'),
      },
      undefined: {
        value: 'undefined',
        label: gettext('label.undefined'),
      },
      offPlatform: {
        value: 'off-platform',
        label: gettext('label.off-platform'),
      }
    };

    return {
      mobile_app: [
        options.mobileApp,
        options.undefined,
        options.offPlatform
      ],
      web: [
        options.web,
        options.undefined,
        options.offPlatform
      ],
      both: [
        options.web,
        options.mobileApp,
        options.undefined
      ],
    }[default_attribution_platform];
  }

  /**
   * Adjust parameters so their keys match kpi service parameters schema and do some custom matching for cohorts
   *
   * @params {Array} osNames
   */
  function adjustParams(osNames) {

    var baseParams = getParams();

    return Moment.getTimezone(baseParams.timezone_id)
      .then(function(timezone) {

        var params = {};
        var map = {
          regions: 'regions',
          countries: 'countries',
          partner_ids: 'partner_ids',
          attribution_type: 'attribution_type',
          rejected: 'exclude_rejected',
          sandbox: 'sandbox',
          conversion_type: 'reattributed',
          attribution_source: 'attribution_source',
          secret_ids: 'secret_ids'
        };

        each(map, function(remote, local) {

          if (!baseParams[local]) {
            return;
          }

          params[remote] = baseParams[local];

          if (Array.isArray(params[remote])) {

            if (isEmpty(params[remote])) {
              delete params[remote];
              return;
            }

            if (isParamExcluded(local)) {
              params[remote] = _excludeParams(params[local]);
            }

            params[remote] = params[remote].join(',');
          }

        });

        if (osNames) {
          params.os_names = _getSelectedOsNames(osNames, baseParams.os_names) || null;
        }

        if (baseParams.platforms) {
          params.platforms = _getPlatform(baseParams.platforms) || null;
        }

        if (baseParams.base && !find(filterConstants.kpiBasis, {token: baseParams.base})) {
          params.events = baseParams.base;
        }

        params.utc_offset = timezone.utc_offset;

        return {
          ...params,
          ...TimeSpan.adjustParams(),
          ...CustomPeriod.getParams()
        };

      });

  }

  /**
   * @ngdoc method
   * @name encodeParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Encode currently set parameters
   *
   * @returns {Object} query parameters object
   */
  function encodeParams() {
    const params = mapValues(getParams(), encodeParam);

    return {
      ...params,
      ...TimeSpan.getParams(),
      ...CustomPeriod.getParams()
    };
  }

  /**
   * @ngdoc method
   * @name encodeParam
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Encode param value into appropriate format
   *
   * @param {*} value value of the parameter
   * @param {string} key key of the value
   * @returns {string} encoded value
   */
  function encodeParam(value, key) {

    if (_arrays.includes(key)) {

      if (isParamExcluded(key) && !isEmpty(value)) {
        value = _excludeParams(value);
      }

      return value && value.length ? uniq(value).join(',') : null;
    } else if (_booleans.includes(key)) {
      return value ? value.toString() : null;
    }

    return value;

  }

  /**
   * @ngdoc method
   * @name _decodeParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Decode parameters into suitable structure used by filters
   *
   * @returns {Object} params object
   * @private
   */
  function _decodeParams() {

    var params = pickBy($location.search(), function(value, key) {
      return _allowedParams.includes(key);
    });

    each(_excludableParams, function(key) {
      if (some(params[key], value => value.startsWith('-'))) {
        addExcludableParam(key);
      }
    });

    each(params, function(param, key) {

      if (_arrays.includes(key)) {
        params[key] = param.split(',');
      } else if (_booleans.includes(key)) {
        params[key] = param === 'true' || false;
      } else if (_numbers.includes(key)) {
        params[key] = parseInt(param);
      }

      if (isParamExcluded(key)) {
        params[key] = _restoreParams(params[key]);
      }

    });

    return params;

  }

  /**
   * @ngdoc method
   * @name isByDefault
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Check if parameters are default ones
   *
   * @returns {Promise|boolean} is by default
   */
  function isByDefault() {

    return _defaults()
      .then(function(defaults) {

        var excludedParams = ['columns', 'pinned', 'plotted', 'scale', 'gkpi', 'granularity'];
        var params = omit(_params, excludedParams.concat('view'));
        var compare = omit(extend(defaults[_type], defaults.graph), excludedParams);

        return isEqual(compare, params);

      });

  }

  /**
   * Get default sort kpi depending on the screen
   *
   * @returns {String}
   */
  function getDefaultSort() {

    const grouping = getParam('grouping');

    if (_type === 'overview' && ['dates', 'week', 'month'].includes(grouping)) {
      return 'name';
    }

    if (_type === 'secrets') {
      return null;
    }

    if (_screen === 'fraud-prevention') {
      return 'rejected_installs';
    }

    if (_screen === 'cost') {
      return 'cost';
    }

    return 'installs';

  }

  /**
   * @ngdoc method
   * @name addPin
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Add token to pinned param array
   *
   * @param {string} token tracker token
   */
  function addPin(token) {

    var pinned = _params.pinned.slice();

    pinned.push(token);

    preserveParam('pinned', pinned);
  }

  /**
   * @ngdoc method
   * @name removePin
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Remove token from pinned param array
   *
   * @param {string} token tracker token
   */
  function removePin(token) {

    var pinned = _params.pinned.slice();

    pull(pinned, token);

    preserveParam('pinned', pinned);

  }

  /**
   * @ngdoc method
   * @name addPlot
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Add token to plotted list
   *
   * @param {string} token tracker token
   */
  function addPlot(token) {

    var plotted = _params.plotted.slice();

    plotted.push(token);

    preserveParam('plotted', plotted);

  }

  /**
   * @ngdoc method
   * @name removePlot
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Remove token from plotted list
   *
   * @param {string} token tracker token
   */
  function removePlot(token) {

    var plotted = _params.plotted.slice();

    pull(plotted, token);

    preserveParam('plotted', plotted);

  }

  /**
   * @ngdoc method
   * @name getOuterParams
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get list of parameters handled outside of the filter
   *
   * @returns {Array} array of parameters
   */
  function getOuterParams() {

    return angular.copy(_outerParams);

  }

  /**
   * @ngdoc method
   * @name resetGrouping
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Reset the default grouping
   *
   * @returns {Promise|string} grouping value
   */
  function resetGrouping() {

    return _defaults()
      .then(function(defaults) {

        preserveParam('grouping', defaults[_type].grouping);

        return defaults[_type].grouping;

      });

  }

  /**
   * @ngdoc method
   * @name getGrouping
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get the current grouping
   *
   * @returns {Object} grouping object
   */
  function getGrouping() {

    return find(Grouping.get(_type), {value: getParam('grouping')});

  }

  /**
   * @ngdoc method
   * @name currentMomentTz
   * @methodOf adjust.common.service:Filter
   *
   * @description
   * Get the current moment timezone
   *
   * @returns {Promise|String} valid moment timezone name
   */
  function currentMomentTz() {

    return Moment.getTimezone(getParam('timezone_id'))
      .then(function(timezone) {

        return timezone.momentTz;

      });

  }

  /**
   * @param {Array} param
   * @returns {Array}
   * @private
   */
  function _excludeParams(param) {
    return map(param, value => `-${value}`);
  }

  /**
   * @param {Array} param
   * @returns {Array}
   * @private
   */
  function _restoreParams(param) {
    return map(param, value => value.replace('-', ''));
  }

  /**
   * @param {String} param
   */
  function addExcludableParam(param) {
    _excludedParams = uniq([..._excludedParams, param]);
  }

  /**
   * @param {String} param
   */
  function removeExcludableParam(param) {
    _excludedParams = without(_excludedParams, param);
  }

  /**
   * @param {String} param
   * @returns {Boolean}
   */
  function isParamExcluded(param) {
    return _excludedParams.includes(param);
  }

  /**
   * @param {Object} params
   */
  function clearEmptyExcludableParams(params) {
    _excludedParams = flow(curriedPick(_excludedParams), curriedOmitBy(isEmpty), keys)(params);
  }
}


export { Filter };
